import { MicroLessonCategory } from '@/types/microLearning';

export const microLessonCategories: MicroLessonCategory[] = [
  {
    id: 'hormones',
    name: 'Hormone Essentials',
    description: 'Quick lessons on hormones and how they work',
    icon: '🧬',
    lessons: [
      {
        id: 'estrogen-101',
        title: 'Estrogen: Your Multitasker',
        category: 'hormones',
        duration: '2 min',
        content: `Estrogen is your body's powerhouse hormone. It doesn't just handle reproduction - it affects over 400 functions in your body.

**What Estrogen Does:**
- Keeps bones strong and dense
- Protects heart health
- Supports brain function and memory
- Maintains skin elasticity
- Regulates body temperature
- Influences mood and energy

**During Menopause:**
Your estrogen levels drop by about 90%. This dramatic shift affects every system estrogen supported - which is why you might experience so many different symptoms.

**The Good News:**
Your body adapts over time, and treatment options can help bridge the gap while you adjust.`,
        keyTakeaway: 'Estrogen affects 400+ body functions - its decline during menopause impacts multiple systems simultaneously.',
        quiz: [
          {
            question: 'What percentage does estrogen drop during menopause?',
            options: ['50%', '70%', '90%', '100%'],
            correctIndex: 2,
            explanation: 'The dramatic decline in estrogen during menopause is why the effects can be so wide-ranging across multiple body systems. Think about what percentage drop would explain symptoms affecting 400+ functions.'
          },
          {
            question: 'Which of these does estrogen NOT support?',
            options: ['Bone strength', 'Heart health', 'Blood type', 'Brain function'],
            correctIndex: 2,
            explanation: 'Think about what changes during menopause when estrogen drops. Consider which of these options is determined by genetics at birth and remains constant throughout your entire life, regardless of hormone levels.'
          },
          {
            question: 'How many body functions does estrogen affect?',
            options: ['About 50', 'Over 400', 'Around 150', 'Less than 100'],
            correctIndex: 1,
            explanation: 'Estrogen is one of the most influential hormones in the human body, affecting everything from bones and brain to heart and skin. Consider why menopause symptoms are so diverse and wide-ranging.'
          },
          {
            question: 'During menopause, does your body eventually adapt to lower estrogen?',
            options: ['No, it never adapts', 'Yes, over time', 'Only with medication', 'Only if you exercise'],
            correctIndex: 1,
            explanation: 'Your body does adapt over time to lower estrogen levels, though the timeline varies. Treatment options can help during the transition.'
          }
        ],
        relatedBadgeId: 'hormone-scholar'
      },
      {
        id: 'progesterone-power',
        title: 'Progesterone: Your Calming Friend',
        category: 'hormones',
        duration: '2 min',
        content: `Progesterone is often overlooked, but it's crucial for your wellbeing. Think of it as your natural chill pill.

**What Progesterone Does:**
- Balances estrogen's effects
- Promotes calm and relaxation
- Supports quality sleep
- Regulates menstrual cycles
- Protects uterine lining

**Why It Matters in Menopause:**
Progesterone often drops before estrogen does. This can cause:
- Sleep problems (it's nature's sedative!)
- Increased anxiety
- Irregular periods during perimenopause
- Mood swings

**Treatment Options:**
If you take HRT and still have your uterus, you'll need progesterone to protect your uterine lining. Some women find it helps with sleep too.`,
        keyTakeaway: 'Progesterone is your natural calming hormone - its decline affects sleep, mood, and anxiety levels.',
        quiz: [
          {
            question: 'What key function does progesterone provide during menopause?',
            options: ['Increases energy', 'Promotes calm and sleep', 'Builds muscle', 'Regulates temperature'],
            correctIndex: 1,
            explanation: 'Progesterone acts on GABA receptors in the brain (like sleep medications) and promotes calm, relaxation, and quality sleep.'
          },
          {
            question: 'When does progesterone typically start declining?',
            options: ['After menopause', 'Before estrogen does', 'Same time as estrogen', 'Never declines'],
            correctIndex: 1,
            explanation: 'Progesterone often drops before estrogen does during perimenopause, which can cause early symptoms like sleep problems and anxiety.'
          },
          {
            question: 'If you take HRT and still have your uterus, do you need progesterone?',
            options: ['No, never needed', 'Yes, to protect uterine lining', 'Only if over 50', 'Only for first year'],
            correctIndex: 1,
            explanation: 'If you take estrogen HRT and have a uterus, you need progesterone to protect your uterine lining from overgrowth.'
          }
        ],
        relatedBadgeId: 'hormone-scholar'
      },
      {
        id: 'testosterone-truth',
        title: 'Yes, Women Need Testosterone Too',
        category: 'hormones',
        duration: '2 min',
        content: `Surprised? Many women are. Testosterone isn't just a "male hormone" - women produce it too, and it's important.

**What Testosterone Does for Women:**
- Maintains muscle mass and strength
- Supports bone density
- Fuels energy and vitality
- Influences libido and sexual function
- Affects mood and confidence

**In Menopause:**
Testosterone gradually declines with age, and menopause can accelerate this. Low levels can cause:
- Persistent fatigue
- Loss of muscle mass
- Decreased libido
- Reduced motivation

**The Controversy:**
Testosterone for women isn't officially approved in many countries, but it's increasingly prescribed "off-label" for low libido and fatigue. Research shows it can help, but requires careful monitoring.`,
        keyTakeaway: 'Women produce testosterone - it supports energy, muscle, libido, and overall vitality.',
        quiz: [
          {
            question: 'Which of these does testosterone NOT support in women?',
            options: ['Muscle mass', 'Energy levels', 'Bone density', 'Menstrual cycles'],
            correctIndex: 3,
            explanation: 'Testosterone is an anabolic hormone - meaning it builds and maintains physical structures and powers metabolic processes. Think about which option involves a different type of hormonal regulation entirely, one that follows a monthly cycle rather than steady maintenance.'
          },
          {
            question: 'Do women naturally produce testosterone?',
            options: ['No, only men do', 'Yes, in smaller amounts', 'Only during menopause', 'Only if taking hormones'],
            correctIndex: 1,
            explanation: 'Consider where testosterone comes from in the body. Both ovaries and adrenal glands can produce hormones. Think about whether this production would be unique to one sex or shared between both.'
          },
          {
            question: 'Is testosterone therapy officially approved for women in many countries?',
            options: ['Yes, widely approved', 'No, but prescribed off-label', 'Yes, but only for men', 'Completely banned'],
            correctIndex: 1,
            explanation: 'Testosterone for women isn\'t officially approved in many countries, but it\'s increasingly prescribed "off-label" for low libido and fatigue with careful monitoring.'
          }
        ],
        relatedBadgeId: 'hormone-scholar'
      }
    ]
  },
  {
    id: 'symptoms',
    name: 'Symptom Mastery',
    description: 'Understanding and managing key symptoms',
    icon: '🎯',
    lessons: [
      {
        id: 'hot-flash-science',
        title: "Hot Flashes: What's Really Happening",
        category: 'symptoms',
        duration: '2 min',
        content: `Hot flashes aren't random - there's real science behind why you suddenly feel like you're on fire.

**The Mechanism:**
Your hypothalamus controls body temperature like a thermostat. Estrogen helps keep this thermostat stable. When estrogen drops:
- Your "thermoneutral zone" (comfortable temperature range) narrows dramatically
- Tiny temperature changes trigger cooling responses
- Your body dilates blood vessels and sweats to cool you down
- It's protective, just inconvenient

**Why They Vary:**
75% of women get hot flashes, but severity varies wildly due to:
- Genetics
- How quickly estrogen drops
- Overall health and lifestyle
- Stress levels

**The Timeline:**
Most women experience hot flashes for 2-7 years, but 10-15% have them for much longer. Treatment can help significantly.`,
        keyTakeaway: "Hot flashes are your body's cooling response to a hypersensitive temperature thermostat caused by dropping estrogen.",
        quiz: [
          {
            question: 'What causes hot flashes during menopause?',
            options: ['Dangerous overheating', 'Random hormone spikes', 'Narrowed thermoneutral zone from low estrogen', 'High blood pressure'],
            correctIndex: 2,
            explanation: 'Dropping estrogen makes your hypothalamus (temperature control center) hypersensitive, narrowing your comfort zone and triggering cooling responses to tiny temperature changes.'
          },
          {
            question: 'What percentage of women experience hot flashes?',
            options: ['25%', '50%', '75%', '95%'],
            correctIndex: 2,
            explanation: 'Hot flashes are one of the most common menopause symptoms, though severity varies widely based on genetics, health, and lifestyle. Think about whether they affect a minority, about half, most, or nearly all women.'
          },
          {
            question: 'How long do most women experience hot flashes?',
            options: ['1-2 months', '2-7 years', '10-20 years', 'Forever'],
            correctIndex: 1,
            explanation: 'Hot flash duration varies significantly between individuals. Some have them briefly, while 10-15% experience them for much longer. Treatment can help significantly throughout the journey.'
          }
        ],
        relatedBadgeId: 'symptom-detective'
      },
      {
        id: 'brain-fog-facts',
        title: 'Brain Fog Is Real (And Temporary)',
        category: 'symptoms',
        duration: '2 min',
        content: `If you've walked into a room and forgotten why, lost words mid-sentence, or struggled to focus - you're experiencing real, hormone-related brain changes.

**Why It Happens:**
- Estrogen fuels brain metabolism and supports memory
- The hippocampus (memory center) has dense estrogen receptors
- Dropping estrogen affects neurotransmitters like acetylcholine
- Your brain is literally reorganizing to function with less estrogen

**It's Not Dementia:**
This is adaptation, not deterioration. Research shows:
- Cognitive function typically stabilizes 1-2 years after menopause
- HRT can help during the transition
- Your brain is working, not broken

**When to Worry:**
If cognitive issues worsen or include significant functional problems, see your doctor to rule out thyroid issues, B12 deficiency, or sleep disorders.`,
        keyTakeaway: "Brain fog is your brain adapting to lower estrogen - it's temporary and manageable, not permanent damage.",
        quiz: [
          {
            question: 'Brain fog during menopause is:',
            options: ['Permanent brain damage', 'A sign of dementia', 'Your brain adapting to lower estrogen', 'Just normal aging'],
            correctIndex: 2,
            explanation: 'Brain fog is your brain reorganizing to function with less estrogen - cognitive function typically stabilizes 1-2 years after menopause.'
          },
          {
            question: 'The hippocampus is important for memory and has:',
            options: ['No estrogen receptors', 'Few estrogen receptors', 'Dense estrogen receptors', 'Only testosterone receptors'],
            correctIndex: 2,
            explanation: 'The hippocampus (memory center) has dense estrogen receptors, which is why memory can be affected when estrogen drops.'
          },
          {
            question: 'When does cognitive function typically stabilize after menopause?',
            options: ['Never', '1-2 years', '5-10 years', 'Immediately'],
            correctIndex: 1,
            explanation: 'As your brain adapts to functioning with lower estrogen, cognitive function stabilizes. This adaptation takes time but isn\'t instantaneous. Think about typical healing and adjustment timeframes for major body changes.'
          }
        ],
        relatedBadgeId: 'symptom-detective'
      },
      {
        id: 'sleep-solution',
        title: 'Why Sleep Becomes So Elusive',
        category: 'symptoms',
        duration: '2 min',
        content: `Sleep problems during menopause aren't "just in your head" - they're hormonal.

**The Science:**
- Progesterone acts on the same brain receptors as sleep medications
- Estrogen helps regulate sleep-wake cycles
- Both dropping = disrupted sleep architecture

**The Triple Threat:**
1. Hormones directly affect sleep quality
2. Night sweats physically wake you up
3. Anxiety can make falling back asleep harder

**The Impact:**
Poor sleep isn't just tiring - it worsens EVERY other menopause symptom:
- Brain fog intensifies
- Mood problems worsen
- Hot flashes increase
- Pain sensitivity rises

**Priority Status:**
Fixing sleep should be a top priority. It's foundational to managing everything else.`,
        keyTakeaway: "Falling progesterone and estrogen directly disrupt your sleep architecture - improving sleep helps every other symptom.",
        quiz: [
          {
            question: 'Why does menopause affect sleep quality?',
            options: ['You need less sleep as you age', 'Progesterone and estrogen directly affect sleep', 'It doesn\'t - just coincidence', 'Only because of stress'],
            correctIndex: 1,
            explanation: 'Progesterone acts on the same brain receptors as sleep medications, and estrogen regulates sleep-wake cycles. Both dropping = disrupted sleep.'
          },
          {
            question: 'Poor sleep during menopause affects:',
            options: ['Only energy levels', 'Every other symptom', 'Nothing else', 'Just mood'],
            correctIndex: 1,
            explanation: 'Poor sleep intensifies brain fog, worsens mood, increases hot flashes, and raises pain sensitivity - making it foundational to address.'
          },
          {
            question: 'What brain receptors does progesterone act on?',
            options: ['The same as sleep medications', 'Dopamine receptors', 'Serotonin receptors only', 'No receptors'],
            correctIndex: 0,
            explanation: 'Progesterone acts on GABA receptors in the brain - the same receptors that sleep medications target - which is why its decline affects sleep so dramatically.'
          }
        ],
        relatedBadgeId: 'symptom-detective'
      },
      {
        id: 'mood-matters',
        title: 'Understanding Mood Changes',
        category: 'symptoms',
        duration: '2 min',
        content: `Feeling irritable, anxious, or unexpectedly tearful? It's not "just emotions" - it's neurochemistry.

**What's Happening:**
Estrogen directly affects:
- Serotonin (mood regulation)
- Dopamine (pleasure and motivation)
- Norepinephrine (energy and focus)

When estrogen drops, these neurotransmitters fluctuate, causing mood changes.

**Common Experiences:**
- Increased irritability ("rage-o-pause")
- Anxiety or panic feelings
- Sadness or depression
- Emotional sensitivity
- Reduced stress tolerance

**This Isn't "All in Your Head:"**
These are real, biological changes. You're not losing your mind or overreacting.

**When to Seek Help:**
If mood changes significantly impact your life, relationships, or daily function - talk to your doctor. You might need HRT, antidepressants, or therapy. There's no shame in getting support.`,
        keyTakeaway: "Mood changes during menopause are neurochemical, not character flaws - they're treatable.",
        quiz: [
          {
            question: 'What causes mood changes during menopause?',
            options: ['Getting older and grumpy', 'Life stress only', 'Estrogen affecting brain neurotransmitters', 'Personality changes'],
            correctIndex: 2,
            explanation: 'Estrogen directly affects serotonin, dopamine, and norepinephrine - the neurotransmitters that regulate mood, pleasure, and energy.'
          },
          {
            question: 'Which neurotransmitters does estrogen affect?',
            options: ['Only serotonin', 'Serotonin, dopamine, and norepinephrine', 'None', 'Only adrenaline'],
            correctIndex: 1,
            explanation: 'Estrogen affects serotonin (mood), dopamine (pleasure/motivation), and norepinephrine (energy/focus) - which is why mood changes are so common.'
          },
          {
            question: 'When should you seek help for mood changes?',
            options: ['Never, just tough it out', 'When they significantly impact your life', 'Only after trying everything else', 'Not until suicidal'],
            correctIndex: 1,
            explanation: 'If mood changes significantly impact your life, relationships, or daily function, talk to your doctor. You might need HRT, antidepressants, or therapy.'
          },
          {
            question: '"Rage-o-pause" refers to:',
            options: ['Pausing when angry', 'Sudden intense irritability', 'Anger at menopause', 'A medical condition'],
            correctIndex: 1,
            explanation: 'Rage-o-pause is a colloquial term for the sudden, intense irritability that many women experience during menopause due to hormonal changes.'
          }
        ],
        relatedBadgeId: 'symptom-detective'
      },
      {
        id: 'weight-wisdom',
        title: 'The Weight Gain Reality',
        category: 'symptoms',
        duration: '2 min',
        content: `That stubborn midlife weight gain isn't your fault - your metabolism literally changes.

**What's Changing:**
- You lose muscle mass (which burns calories)
- Metabolism slows about 5% per decade
- Fat distribution shifts to your midsection
- Insulin sensitivity decreases
- Activity levels often drop

**The Menopause Effect:**
Estrogen loss specifically:
- Reduces metabolic rate
- Changes where your body stores fat (hello, belly fat)
- Affects hunger and satiety signals
- Makes it harder to build muscle

**The Truth:**
Average weight gain is 5-10 pounds during the transition. Some women gain more, some less - it's highly individual.

**What Helps:**
- Strength training (crucial for maintaining muscle)
- Protein at every meal
- Managing cortisol (stress hormone)
- Adequate sleep
- Realistic expectations (your body is changing)`,
        keyTakeaway: 'Menopause changes metabolism and fat distribution - strength training and protein are your best allies.',
        quiz: [
          {
            question: 'What is the MOST important exercise type during menopause?',
            options: ['Cardio only', 'Strength training', 'Yoga', 'Walking'],
            correctIndex: 1,
            explanation: 'Strength training 2-3x/week is crucial to combat muscle loss, maintain metabolism, strengthen bones, and improve insulin sensitivity.'
          },
          {
            question: 'How much does metabolism slow per decade?',
            options: ['1%', '5%', '10%', '20%'],
            correctIndex: 1,
            explanation: 'Metabolism naturally slows as we age, which explains why weight management becomes more challenging after age 40. The slowdown is gradual but significant enough to notice over time.'
          },
          {
            question: 'During menopause, fat distribution shifts to:',
            options: ['Hips and thighs', 'Midsection/belly', 'Arms', 'Legs'],
            correctIndex: 1,
            explanation: 'Estrogen loss causes fat distribution to shift to the midsection (belly fat), even if your weight stays the same.'
          },
          {
            question: 'Average weight gain during menopause transition is:',
            options: ['0-2 pounds', '5-10 pounds', '15-20 pounds', '25+ pounds'],
            correctIndex: 1,
            explanation: 'Weight gain during the transition varies widely based on lifestyle, genetics, and individual factors. It\'s noticeable for most women but typically manageable with lifestyle adjustments.'
          }
        ],
        relatedBadgeId: 'symptom-detective'
      }
    ]
  },
  {
    id: 'lifestyle',
    name: 'Lifestyle Strategies',
    description: 'Practical daily habits that make a difference',
    icon: '⚡',
    lessons: [
      {
        id: 'nutrition-basics',
        title: 'Menopause Nutrition Essentials',
        category: 'lifestyle',
        duration: '3 min',
        content: `Your nutritional needs shift during menopause. Here's what your body needs now.

**Protein Priority:**
- Aim for 25-30g per meal
- Supports muscle maintenance (you're losing muscle faster now)
- Helps with satiety and blood sugar
- Sources: eggs, fish, chicken, legumes, Greek yogurt

**Calcium & Vitamin D:**
- 1200mg calcium daily (from food + supplements)
- 1000-2000 IU vitamin D
- Critical for bone health (estrogen loss accelerates bone loss)

**Omega-3 Fatty Acids:**
- Supports brain health and reduces inflammation
- Aim for fatty fish 2-3x/week
- Or supplement with 1000-2000mg EPA/DHA daily

**Phytoestrogens:**
- Plant compounds that weakly mimic estrogen
- Found in soy, flaxseed, chickpeas
- May help some women with mild symptoms

**What to Limit:**
- Alcohol (worsens hot flashes, disrupts sleep)
- Caffeine (especially after 2pm)
- Processed foods high in sugar and salt`,
        keyTakeaway: 'Prioritize protein, calcium, vitamin D, and omega-3s - your body needs these more than ever during menopause.',
        quiz: [
          {
            question: 'How much protein should you aim for per meal during menopause?',
            options: ['10-15g', '25-30g', '40-50g', '60+ g'],
            correctIndex: 1,
            explanation: '25-30g protein per meal helps maintain muscle mass (which you lose faster during menopause) and supports satiety and blood sugar.'
          },
          {
            question: 'How much calcium should you get daily during menopause?',
            options: ['500mg', '1200mg', '2000mg', '3000mg'],
            correctIndex: 1,
            explanation: '1200mg calcium daily (from food and supplements) is recommended during menopause for bone health, as estrogen loss accelerates bone loss.'
          },
          {
            question: 'Phytoestrogens are found in:',
            options: ['Meat', 'Soy and flaxseed', 'Dairy', 'Processed foods'],
            correctIndex: 1,
            explanation: 'Phytoestrogens are plant compounds in soy, flaxseed, and chickpeas that weakly mimic estrogen and may help some women with mild symptoms.'
          },
          {
            question: 'What should you limit during menopause?',
            options: ['Water', 'Vegetables', 'Alcohol and caffeine', 'Protein'],
            correctIndex: 2,
            explanation: 'Alcohol worsens hot flashes and disrupts sleep, while caffeine (especially after 2pm) can worsen sleep problems and anxiety.'
          }
        ],
        relatedBadgeId: 'nutrition-guru'
      },
      {
        id: 'exercise-essentials',
        title: 'The Right Exercise for Menopause',
        category: 'lifestyle',
        duration: '3 min',
        content: `Exercise becomes more important during menopause, but the type matters.

**Strength Training (Most Important!):**
- 2-3x per week minimum
- Builds muscle (which you're losing)
- Boosts metabolism
- Strengthens bones
- Improves insulin sensitivity
- Don't be afraid of weights!

**Cardio That Helps:**
- 150 minutes moderate activity per week
- Walking, swimming, cycling
- Good for heart health and mood
- But not enough on its own

**Balance & Flexibility:**
- Yoga, Pilates, tai chi
- Helps with stress and sleep
- Prevents falls as you age
- Improves joint mobility

**What to Avoid:**
- Over-exercising (raises cortisol)
- Only doing cardio (need strength training!)
- Being sedentary

**Start Where You Are:**
Even 10 minutes counts. Consistency beats intensity. Find activities you actually enjoy.`,
        keyTakeaway: 'Strength training 2-3x/week is non-negotiable - it combats muscle loss, bone loss, and metabolic slowdown.',
        quiz: [
          {
            question: 'How often should you do strength training during menopause?',
            options: ['Once a month', '2-3 times per week', 'Every day', 'Only if you want to'],
            correctIndex: 1,
            explanation: 'Strength training 2-3x/week minimum is essential to build muscle, boost metabolism, strengthen bones, and combat menopause-related muscle loss.'
          },
          {
            question: 'How much moderate cardio per week is recommended?',
            options: ['30 minutes', '150 minutes', '300 minutes', '500 minutes'],
            correctIndex: 1,
            explanation: '150 minutes of moderate cardio per week is recommended for heart health and mood, but it\'s not enough on its own - you also need strength training.'
          },
          {
            question: 'What is a risk of over-exercising during menopause?',
            options: ['Raising cortisol', 'Building too much muscle', 'Sleeping too much', 'Eating too much'],
            correctIndex: 0,
            explanation: 'Over-exercising raises cortisol (stress hormone), which can worsen menopause symptoms. Balance is key - don\'t go overboard.'
          }
        ],
        relatedBadgeId: 'movement-champion'
      },
      {
        id: 'stress-management',
        title: 'Managing Stress in Menopause',
        category: 'lifestyle',
        duration: '2 min',
        content: `Stress hits harder during menopause. Your stress resilience literally decreases.

**Why Stress Is Worse Now:**
- Estrogen helps regulate cortisol (stress hormone)
- Without enough estrogen, cortisol runs higher
- High cortisol worsens hot flashes, sleep, weight gain, and mood

**The Cortisol-Menopause Cycle:**
High stress → High cortisol → Worse symptoms → More stress → Higher cortisol...

**Breaking the Cycle:**
- Deep breathing (activates calming nervous system)
- Regular exercise (burns off stress hormones)
- Adequate sleep (regulates cortisol)
- Saying "no" more often (protect your energy)
- Meditation or mindfulness (10 min/day helps)

**Practical Stress Reducers:**
- Morning sunlight exposure
- Time in nature
- Social connection
- Creative activities
- Professional help when needed

**Remember:**
You're not "weak" - you're managing a significant biological transition while dealing with life. Be gentler with yourself.`,
        keyTakeaway: 'Stress tolerance decreases during menopause due to hormonal changes - stress management becomes essential, not optional.',
        quiz: [
          {
            question: 'Why does stress hit harder during menopause?',
            options: ['You\'re just getting older', 'Low estrogen allows cortisol to run higher', 'It doesn\'t - just perception', 'Less social support'],
            correctIndex: 1,
            explanation: 'Estrogen helps regulate cortisol (stress hormone). Without enough estrogen, cortisol runs higher, making you more stress-sensitive.'
          },
          {
            question: 'High cortisol during menopause can worsen:',
            options: ['Only mood', 'Hot flashes, sleep, weight gain, and mood', 'Nothing', 'Just energy'],
            correctIndex: 1,
            explanation: 'High cortisol creates a vicious cycle - it worsens hot flashes, disrupts sleep, promotes belly fat gain, and affects mood.'
          },
          {
            question: 'What is a practical stress reducer?',
            options: ['Working longer hours', 'Morning sunlight exposure', 'Drinking more caffeine', 'Sleeping less'],
            correctIndex: 1,
            explanation: 'Morning sunlight exposure helps regulate cortisol rhythms and improve mood. Other practical reducers include nature time, social connection, and saying "no" more often.'
          }
        ],
        relatedBadgeId: 'calm-master'
      },
      {
        id: 'sleep-hygiene',
        title: 'Sleep Hygiene That Actually Works',
        category: 'lifestyle',
        duration: '3 min',
        content: `Good sleep hygiene is crucial during menopause. Here's what works.

**The 10-3-2-1-0 Rule:**
- 10 hours before bed: Last caffeine
- 3 hours before: Last large meal
- 2 hours before: No work/stress
- 1 hour before: No screens
- 0: Snooze button uses (wake at same time daily)

**Temperature Control:**
- Keep bedroom at 65-68°F
- Use cooling sheets/pillow
- Wear breathable pajamas
- Keep fan nearby

**The Routine:**
Same sequence nightly:
1. Dim lights 1 hour before bed
2. Warm bath or shower
3. Gentle stretching
4. Reading or meditation
5. Bed at same time

**What Helps:**
- Magnesium glycinate (200-400mg)
- Blackout curtains
- White noise machine
- No TV in bedroom

**What Doesn't:**
- Alcohol (disrupts sleep architecture)
- Late exercise (raises cortisol)
- Inconsistent schedule
- Bright lights before bed`,
        keyTakeaway: 'Consistent sleep routine + cool bedroom + magnesium = better sleep during menopause.',
        quiz: [
          {
            question: 'What is the ideal bedroom temperature for menopause sleep?',
            options: ['70-75°F', '65-68°F', '60-62°F', 'Whatever feels comfortable'],
            correctIndex: 1,
            explanation: '65-68°F is optimal for sleep during menopause. A cool room helps counteract night sweats and improves sleep quality.'
          },
          {
            question: 'In the 10-3-2-1-0 sleep rule, the "10" refers to:',
            options: ['Hours of sleep', '10 hours before bed: last caffeine', '10 pm bedtime', '10 minutes of meditation'],
            correctIndex: 1,
            explanation: 'The 10-3-2-1-0 rule: 10 hours before bed is the last caffeine, 3 hours is last large meal, 2 hours is no work/stress, 1 hour is no screens, 0 is snooze button uses.'
          },
          {
            question: 'What supplement may help sleep during menopause?',
            options: ['Caffeine', 'Magnesium glycinate', 'Sugar', 'Vitamin C'],
            correctIndex: 1,
            explanation: 'Magnesium glycinate (200-400mg) can help with sleep quality and muscle relaxation during menopause.'
          },
          {
            question: 'What disrupts sleep architecture?',
            options: ['Water', 'Alcohol', 'Vegetables', 'Morning exercise'],
            correctIndex: 1,
            explanation: 'Alcohol disrupts sleep architecture even if it makes you drowsy initially - it prevents deep, restorative sleep.'
          }
        ],
        relatedBadgeId: 'sleep-champion'
      }
    ]
  },
  {
    id: 'treatments',
    name: 'Treatment Options',
    description: 'Understanding your choices for managing menopause',
    icon: '💊',
    lessons: [
      {
        id: 'hrt-basics',
        title: 'HRT: The Most Effective Option',
        category: 'treatments',
        duration: '3 min',
        content: `**HRT (Hormone Replacement Therapy — medicine that tops up the hormones your body is making less of)** is the most effective option we have for the classic menopause symptoms. Here's the short version.

**What it does**
Replaces the hormones your body is making less of:
- Oestrogen (usually oestradiol)
- Progesterone (if you still have your uterus)
- Sometimes a small amount of testosterone (for energy or libido)

**Who it helps most**
- Hot flushes and night sweats
- Broken sleep
- Vaginal dryness and discomfort with sex
- Mood dips and brain fog linked to the hormone shift
- Long-term bone protection

**Types**
- Patches, gels, sprays (through the skin) — often the first choice today
- Tablets — simple, but processed through the liver
- Vaginal oestrogen (cream, tablet, ring) — a low-dose local option that's safe for most women, even many who can't take other forms

**The safety story, honestly**
The scary headlines came from one 2002 study (WHI) done mostly in women in their 60s, using an older formulation. For most women **under 60 or within 10 years of their last period**, the benefits usually outweigh the risks. Any change in breast cancer risk is small and depends on the type and length of treatment.

**When it's usually not the right fit**
- A personal history of breast cancer
- A history of blood clots
- Active liver disease
- Unexplained vaginal bleeding (needs investigating first)

Even in these situations, low-dose vaginal oestrogen may still be an option — worth asking a menopause-aware doctor.

**Want the full picture?**
Read the plain-English guide inside Her Season: **HRT, Explained in Plain English** → /article/hrt-explained-plain-english`,
        keyTakeaway: "HRT is the most effective option for menopause symptoms. For most women under 60 or within 10 years of menopause, benefits usually outweigh risks — a conversation to have with your own doctor.",
        quiz: [
          {
            question: 'For women under 60 or within 10 years of menopause, HRT:',
            options: ['Is always dangerous', 'Benefits typically outweigh risks for most women', 'Causes cancer', 'Never works'],
            correctIndex: 1,
            explanation: 'Current guidance from NICE and The Menopause Society says that for most women under 60 or within 10 years of menopause, HRT benefits generally outweigh the risks.'
          },
          {
            question: 'HRT is most effective for which symptoms?',
            options: ['Only hot flushes', 'Hot flushes, sleep, mood, and bone protection', 'Just mood', 'Only vaginal dryness'],
            correctIndex: 1,
            explanation: 'HRT is the most effective option we have for hot flushes, night sweats, broken sleep, mood dips linked to the hormone shift, brain fog, and long-term bone protection.'
          },
          {
            question: 'Who cannot take HRT?',
            options: ['Anyone over 50', 'Women with personal history of breast cancer', 'All women', 'No one'],
            correctIndex: 1,
            explanation: 'Contraindications include personal history of breast cancer, blood clot disorders, active liver disease, and unexplained vaginal bleeding. Everyone else should discuss it with their doctor.'
          },
          {
            question: 'Modern HRT formulations compared to old studies are:',
            options: ['Exactly the same', 'Different and safer', 'More dangerous', 'Not available'],
            correctIndex: 1,
            explanation: 'Modern HRT uses different formulations (bioidentical hormones, transdermal delivery) that are safer than the synthetic hormones used in older studies.'
          }
        ],
        relatedBadgeId: 'treatment-explorer'
      },
      {
        id: 'non-hormonal-options',
        title: 'Non-Hormonal Treatments That Work',
        category: 'treatments',
        duration: '3 min',
        content: `Can't or don't want to take HRT? You have evidence-based options.

**Medications:**
- SSRIs/SNRIs: Reduce hot flashes 50-60% (paroxetine, venlafaxine)
- Gabapentin: Helps hot flashes and sleep
- Oxybutynin: Reduces hot flashes
- Fezolinetant (new!): Non-hormonal, very effective

**Cognitive Behavioral Therapy (CBT):**
- Proven effective for hot flashes and sleep
- Helps with mood and anxiety
- Changes how you respond to symptoms
- No side effects

**Hypnotherapy:**
- Clinical hypnotherapy reduces hot flashes
- 5-6 sessions can help
- Evidence-based, not "woo-woo"

**Supplements with Some Evidence:**
- Black cohosh: May help hot flashes (inconsistent evidence)
- Omega-3s: Supports brain health
- Magnesium: Helps sleep
- Vitamin E: Modest effect on hot flashes

**What Doesn't Work:**
- Red clover (no better than placebo)
- Dong quai
- Evening primrose oil
- Ginseng

**Important:**
Non-hormonal doesn't mean risk-free. Talk to your doctor about any supplements or medications.`,
        keyTakeaway: 'Effective non-hormonal options exist: SSRIs, gabapentin, CBT, and the new drug fezolinetant.',
        quiz: [
          {
            question: 'Which is an evidence-based non-hormonal treatment for hot flashes?',
            options: ['Red clover', 'SSRIs/SNRIs', 'Evening primrose oil', 'Dong quai'],
            correctIndex: 1,
            explanation: 'SSRIs/SNRIs reduce hot flashes by 50-60%. Red clover, evening primrose, and dong quai have not shown consistent benefits in research.'
          },
          {
            question: 'How much do SSRIs/SNRIs reduce hot flashes?',
            options: ['10-20%', '50-60%', '90-100%', 'They don\'t help'],
            correctIndex: 1,
            explanation: 'SSRIs/SNRIs (like paroxetine and venlafaxine) reduce hot flashes by 50-60%, making them an effective non-hormonal option.'
          },
          {
            question: 'Which new non-hormonal drug is very effective for hot flashes?',
            options: ['Aspirin', 'Fezolinetant', 'Ibuprofen', 'Vitamin E'],
            correctIndex: 1,
            explanation: 'Fezolinetant is a new non-hormonal medication specifically approved for hot flashes and is very effective.'
          },
          {
            question: 'Does CBT (Cognitive Behavioral Therapy) help with menopause symptoms?',
            options: ['No evidence', 'Yes, proven for hot flashes and sleep', 'Only for young women', 'Makes them worse'],
            correctIndex: 1,
            explanation: 'CBT is proven effective for hot flashes, sleep problems, and mood/anxiety. It changes how you respond to symptoms with no side effects.'
          }
        ],
        relatedBadgeId: 'treatment-explorer'
      },
      {
        id: 'vaginal-health',
        title: "Vaginal Health: Don't Suffer in Silence",
        category: 'treatments',
        duration: '2 min',
        content: `Vaginal and urinary symptoms affect 50-70% of postmenopausal women and WORSEN over time without treatment.

**Genitourinary Syndrome of Menopause (GSM):**
Includes:
- Vaginal dryness
- Burning or irritation
- Pain with sex
- Recurrent UTIs
- Urinary urgency
- Vaginal laxity

**Why It Happens:**
Estrogen keeps vaginal tissue:
- Thick and elastic
- Well-lubricated
- Slightly acidic (protects from infections)

Without estrogen, tissues thin, dry out, and pH rises.

**Treatment Options:**
1. Vaginal estrogen (BEST option)
   - Creams, tablets, or ring
   - Minimal absorption
   - Safe even if you can't take systemic HRT
   - Use long-term

2. Vaginal moisturizers
   - Use 2-3x weekly
   - Different from lubricants
   - Rehydrate tissues

3. Lubricants
   - Use during sex
   - Water or silicone-based
   - Avoid glycerin (irritating)

**Talk to Your Doctor:**
75% of women don't discuss this with their doctor. Don't be embarrassed - it's medical, not personal.`,
        keyTakeaway: "Vaginal estrogen is safe, effective, and essential for treating genitourinary symptoms - it's not optional.",
        quiz: [
          {
            question: 'Vaginal estrogen is:',
            options: ['Only for women who can\'t take HRT', 'Dangerous and avoidable', 'Safe even if you can\'t take systemic HRT', 'A last resort'],
            correctIndex: 2,
            explanation: 'Vaginal estrogen has minimal absorption and is safe for most women, even those who can\'t take systemic HRT. It\'s essential for vaginal and urinary health.'
          },
          {
            question: 'What percentage of postmenopausal women experience vaginal/urinary symptoms?',
            options: ['10-20%', '30-40%', '50-70%', '80-90%'],
            correctIndex: 2,
            explanation: 'Vaginal and urinary symptoms affect 50-70% of postmenopausal women and worsen over time without treatment.'
          },
          {
            question: 'What does GSM stand for?',
            options: ['General Symptom Management', 'Genitourinary Syndrome of Menopause', 'Good Sleep Method', 'Growth Support Mechanism'],
            correctIndex: 1,
            explanation: 'GSM (Genitourinary Syndrome of Menopause) includes vaginal dryness, burning, pain with sex, recurrent UTIs, and urinary urgency.'
          },
          {
            question: 'What percentage of women discuss vaginal symptoms with their doctor?',
            options: ['75%', '50%', '25%', '90%'],
            correctIndex: 2,
            explanation: '75% of women DON\'T discuss vaginal symptoms with their doctor due to embarrassment - but it\'s medical, not personal, and very treatable.'
          }
        ],
        relatedBadgeId: 'treatment-explorer'
      }
    ]
  },
  {
    id: 'mental-health',
    name: 'Mental Wellness',
    description: 'Supporting your emotional and psychological health',
    icon: '🧘‍♀️',
    lessons: [
      {
        id: 'mood-mastery',
        title: 'Managing Mood Swings',
        category: 'mental-health',
        duration: '2 min',
        content: `Mood swings during menopause are real and manageable.

**What's Happening:**
Your brain chemistry is changing:
- Estrogen affects serotonin production
- Neurotransmitter fluctuations cause mood shifts
- Sleep deprivation makes everything worse
- Life stressors compound the problem

**Common Experiences:**
- "Rage-o-pause" (sudden intense irritability)
- Crying at commercials
- Anxiety out of nowhere
- Feeling "not yourself"
- Reduced stress tolerance

**What Helps:**
1. **HRT**: Most effective for mood symptoms related to menopause
2. **Exercise**: Natural mood stabilizer and anxiety reducer
3. **Sleep**: Priority #1 for mood regulation
4. **Therapy**: CBT especially effective
5. **Antidepressants**: If needed, they work

**When to Get Help:**
- Thoughts of self-harm
- Can't function at work/home
- Relationships suffering significantly
- Symptoms lasting more than 2 weeks

**Remember:**
You're not "crazy" or "difficult." Your brain chemistry is in flux. This is medical, and it's treatable.`,
        keyTakeaway: 'Mood changes are neurochemical, not character flaws - they respond to treatment including HRT, exercise, and therapy.',
        quiz: [
          {
            question: 'What is the most effective treatment for menopause-related mood changes?',
            options: ['Just tough it out', 'HRT (for hormonal mood changes)', 'Avoid all stress', 'Drink more water'],
            correctIndex: 1,
            explanation: 'HRT is most effective for mood symptoms related to hormonal changes. Antidepressants, exercise, and therapy also help significantly.'
          },
          {
            question: 'When should you seek help for mood changes?',
            options: ['Never', 'When they significantly impact your life', 'Only if suicidal', 'After 10 years'],
            correctIndex: 1,
            explanation: 'Seek help when mood changes significantly impact your life, work, or relationships - or if symptoms last more than 2 weeks. Don\'t wait for a crisis.'
          },
          {
            question: 'What makes menopause mood symptoms worse?',
            options: ['Good sleep', 'Sleep deprivation', 'Exercise', 'Therapy'],
            correctIndex: 1,
            explanation: 'Sleep deprivation makes every mood symptom worse. Poor sleep intensifies irritability, anxiety, and depression during menopause.'
          }
        ],
        relatedBadgeId: 'mindfulness-maven'
      },
      {
        id: 'anxiety-solutions',
        title: 'Taming Menopause Anxiety',
        category: 'mental-health',
        duration: '2 min',
        content: `New or worsening anxiety during menopause is incredibly common.

**Why Anxiety Increases:**
- Estrogen has calming effects on the brain
- Progesterone works on anti-anxiety receptors
- Loss of both = more anxiety
- Plus: you're not sleeping, having hot flashes, and feeling stressed

**Types of Anxiety:**
- Generalized worry
- Panic attacks (new or returning)
- Social anxiety
- Health anxiety
- Morning anxiety

**Effective Strategies:**
1. **Breathing techniques**: 4-7-8 breathing calms nervous system
2. **Exercise**: Burns off stress hormones
3. **Limit caffeine**: Makes anxiety worse
4. **Magnesium**: Helps calm nervous system
5. **Therapy**: CBT highly effective for anxiety
6. **Meditation**: Even 10 min/day helps
7. **HRT**: Often reduces anxiety significantly

**When to Consider Medication:**
If anxiety significantly impacts your life, anti-anxiety medications or SSRIs can help. Don't suffer needlessly.

**Quick Anxiety Relief:**
- 4-7-8 breathing (in for 4, hold for 7, out for 8)
- Cold water on face
- Progressive muscle relaxation
- Name 5 things you can see, 4 you can touch, 3 you can hear`,
        keyTakeaway: 'Menopause anxiety is hormonal and treatable - breathing techniques, exercise, and HRT can all help significantly.',
        quiz: [
          {
            question: 'The 4-7-8 breathing technique for anxiety is:',
            options: ['Breathe in 4, hold 7, out 8', 'Do it 4-7-8 times daily', 'Take 4-7-8 deep breaths', '4-7-8 minutes of breathing'],
            correctIndex: 0,
            explanation: '4-7-8 breathing: Inhale for 4 counts, hold for 7, exhale for 8. This activates your calming nervous system and reduces anxiety quickly.'
          },
          {
            question: 'Why does anxiety increase during menopause?',
            options: ['You\'re just worried', 'Loss of estrogen and progesterone affects anti-anxiety brain receptors', 'Life is more stressful', 'Getting older'],
            correctIndex: 1,
            explanation: 'Estrogen has calming effects and progesterone works on anti-anxiety receptors. Loss of both hormones increases anxiety biologically.'
          },
          {
            question: 'What should you limit if you have menopause anxiety?',
            options: ['Water', 'Exercise', 'Caffeine', 'Sleep'],
            correctIndex: 2,
            explanation: 'Caffeine makes anxiety worse during menopause. Limiting or avoiding it can significantly reduce anxiety symptoms.'
          },
          {
            question: 'A quick anxiety relief technique is:',
            options: ['Drink coffee', 'Cold water on face', 'Watch TV', 'Eat sugar'],
            correctIndex: 1,
            explanation: 'Cold water on your face activates the dive reflex, which quickly calms your nervous system. Other quick techniques include 4-7-8 breathing and progressive muscle relaxation.'
          }
        ],
        relatedBadgeId: 'mindfulness-maven'
      },
      {
        id: 'self-compassion',
        title: 'The Power of Self-Compassion',
        category: 'mental-health',
        duration: '2 min',
        content: `You're navigating a major life transition. Be kinder to yourself.

**What Is Self-Compassion?**
Treating yourself with the same kindness you'd offer a friend going through a hard time.

**Why It Matters Now:**
During menopause, women often:
- Judge themselves harshly for symptoms
- Feel guilty about mood changes
- Criticize their changing bodies
- Push through instead of resting
- Minimize their struggles

**The Self-Compassion Practice:**
When you're struggling, say:
1. "This is hard right now" (acknowledge difficulty)
2. "I'm not alone - millions of women experience this" (common humanity)
3. "May I be kind to myself" (self-kindness)

**Practical Self-Compassion:**
- Rest when tired (you're not lazy)
- Ask for help (you're not weak)
- Cry when needed (you're not dramatic)
- Set boundaries (you're not selfish)
- Prioritize yourself (you're not mean)

**What Changes:**
Self-compassion reduces:
- Depression
- Anxiety
- Stress
- Self-criticism

And increases:
- Resilience
- Life satisfaction
- Ability to cope

**Your Mantra:**
"I'm doing my best with what I have, in circumstances I didn't choose. That's enough."`,
        keyTakeaway: "Self-compassion during menopause isn't self-indulgence - it's essential for mental health and resilience.",
        quiz: [
          {
            question: 'Self-compassion during menopause means:',
            options: ['Being lazy and avoiding responsibility', 'Treating yourself as you would a friend', 'Making excuses for everything', 'Never pushing yourself'],
            correctIndex: 1,
            explanation: 'Self-compassion means treating yourself with kindness during difficulty - just as you would support a friend going through menopause.'
          },
          {
            question: 'The three elements of self-compassion practice are:',
            options: ['Eat, sleep, exercise', 'Acknowledge difficulty, common humanity, self-kindness', 'Work, rest, play', 'Think, feel, act'],
            correctIndex: 1,
            explanation: 'Self-compassion practice: 1) Acknowledge this is hard, 2) Remember you\'re not alone - millions experience this, 3) Be kind to yourself.'
          },
          {
            question: 'Self-compassion research shows it reduces:',
            options: ['Only sadness', 'Depression, anxiety, and stress', 'Nothing', 'Just guilt'],
            correctIndex: 1,
            explanation: 'Research shows self-compassion reduces depression, anxiety, stress, and self-criticism while increasing resilience and life satisfaction.'
          },
          {
            question: 'Which is an example of self-compassion?',
            options: ['Pushing through exhaustion', 'Resting when tired', 'Never asking for help', 'Criticizing yourself'],
            correctIndex: 1,
            explanation: 'Resting when tired is self-compassion - you\'re not lazy, you\'re listening to your body during a major biological transition.'
          }
        ],
        relatedBadgeId: 'mindfulness-maven'
      }
    ]
  }
];
